import type {TemplateSubmissionFlowInfo, TemplateSubmissionFlowReducerState} from '@Components/template-submission-flow/template-submission-flow.types';
import {SubmissionFlowStep} from '@Components/template-submission-flow/template-submission-flow.types';
import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import {templateSubmissionFlowExtraReducers} from '@Components/template-submission-flow/template-submission-flow-extra-reducer';

const initialState: TemplateSubmissionFlowReducerState = {
  selectedPosterHashedId: '',
  selectedPosterPreviewUrl: '',
  posterColor: '',
  currentStep: SubmissionFlowStep.DETAILS,
  nextStep: SubmissionFlowStep.DETAILS,
  posterType: '',
  templateName: '',
  templateTags: [],
  designerProfileUri: '',
  isTermsAndConditionsSelected: true,
  isAcknowledgementSelected: true,
  isTemplateAiTaggingEnabled: false,
  isSubmitting: false,
  templateNameInput: '',
  selectedTemplateNameSuggestionPill: '',
  isAITemplateNameGenerating: false,
  isFieldsErrorState: false,
  currentPreviewTimestamp: undefined,
  pagesTimeInfo: [],
};

const templateSubmissionFlowSlice = createSlice({
  name: 'templateSubmissionFlow',
  initialState,
  reducers: {
    setInitialState: (state, action: PayloadAction<TemplateSubmissionFlowInfo>) => {
      state.selectedPosterHashedId = action.payload.selectedPosterHashedId;
      state.selectedPosterPreviewUrl = action.payload.selectedPosterPreviewUrl;
      state.posterColor = action.payload.posterColor;
      state.posterType = action.payload.posterType;
      state.designerProfileUri = action.payload.designerProfileUri;
      state.isTemplateAiTaggingEnabled = action.payload.isTemplateAiTaggingEnabled;
      state.templateName = action.payload.templateName;
      state.pagesTimeInfo = action.payload.pagesTimeInfo;
    },
    incrementSteps: (state) => {
      state.currentStep += 1;
      state.nextStep += 1;
    },
    decrementSteps: (state) => {
      state.currentStep -= 1;
      state.nextStep -= 1;
    },
    setTemplateName: (state, action: PayloadAction<string>) => {
      state.templateName = action.payload;
    },
    setTemplateTag: (state, action: PayloadAction<string>) => {
      state.templateTags.push(action.payload);
    },
    removeSelectedTemplateTag: (state, action: PayloadAction<string>) => {
      const index = state.templateTags.indexOf(action.payload);
      if (index > -1) {
        state.templateTags.splice(index, 1);
      }
    },
    popTemplateTag: (state) => {
      state.templateTags.pop();
    },
    setTermsAndConditionsSelectionState: (state, action: PayloadAction<boolean>) => {
      state.isTermsAndConditionsSelected = action.payload;
    },
    setAcknowledgementSelectionState: (state, action: PayloadAction<boolean>) => {
      state.isAcknowledgementSelected = action.payload;
    },
    setIsSubmitting: (state, action: PayloadAction<boolean>) => {
      state.isSubmitting = action.payload;
    },
    setTemplateNameInput: (state, action: PayloadAction<string>) => {
      state.templateNameInput = action.payload;
    },
    setSelectedTemplateNameSuggestionPill: (state, action: PayloadAction<string>) => {
      state.selectedTemplateNameSuggestionPill = action.payload;
    },
    onAITemplateNameGeneratingUpdate: (state, action: PayloadAction<boolean>): void => {
      state.isAITemplateNameGenerating = action.payload;
    },
    setIsFieldsErrorState: (state, action: PayloadAction<boolean>) => {
      state.isFieldsErrorState = action.payload;
    },
    setPosterPreviewUrl: (state, action: PayloadAction<string>) => {
      state.selectedPosterPreviewUrl = action.payload;
    },
    setCurrentPreviewTimestamp: (state, action: PayloadAction<number | undefined>) => {
      state.currentPreviewTimestamp = action.payload;
    },
  },
  extraReducers: templateSubmissionFlowExtraReducers,
});

export const {
  setInitialState,
  setTemplateName,
  setTemplateTag,
  removeSelectedTemplateTag,
  popTemplateTag,
  incrementSteps,
  decrementSteps,
  setTermsAndConditionsSelectionState,
  setAcknowledgementSelectionState,
  setTemplateNameInput,
  setSelectedTemplateNameSuggestionPill,
  setIsSubmitting,
  onAITemplateNameGeneratingUpdate,
  setIsFieldsErrorState,
  setPosterPreviewUrl,
  setCurrentPreviewTimestamp,
} = templateSubmissionFlowSlice.actions;
export const templateSubmissionFlowReducer = templateSubmissionFlowSlice.reducer;
